import { MoreHorizontal, Pencil, ShieldAlert, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Button } from '../../../../components/Button';
import { Chip } from '../../../../components/Chip';
import { Field } from '../../../../components/Field';
import { Input, Textarea } from '../../../../components/Input';
import { MenuGroup, MenuItem, Popover, usePopover } from '../../../../components/Popover';
import { useToasts } from '../../../../stores/toasts';
import { useDeleteRole, type RoleDetail } from '../../api/roles-api';
import { peopleText } from './RoleList';
import type { RoleDraft } from './role-editor-model';
import styles from './RoleHeader.module.css';

// Roles A: the role's name, where it came from, who it applies to, and its ⋯ menu (Rename, and Delete
// for a copy), which opens beside its button. A rename joins the other unsaved changes: nothing on
// this page saves until Save.

const MENU_WIDTH = 320;

/** "Copied from X" or "Started blank" for a copy (ADR 0085); a standard role says so. */
export function originLine(role: Pick<RoleDetail, 'isSystem' | 'copiedFromLabel'>): string {
  if (role.isSystem) return 'A standard role. Changes apply everywhere it is used.';
  return role.copiedFromLabel ? `Copied from ${role.copiedFromLabel}` : 'Started blank';
}

interface RoleHeaderProps {
  readonly role: RoleDetail;
  readonly draft: RoleDraft;
  readonly canEdit: boolean;
  readonly onRename: (label: string, description: string) => void;
  readonly onDeleted: () => void;
}

export function RoleHeader({ role, draft, canEdit, onRename, onDeleted }: RoleHeaderProps): ReactNode {
  const { anchor, triggerRef, isOpen, open, close: closeMenu } = usePopover();
  const [isRenaming, setIsRenaming] = useState(false);
  const remove = useDeleteRole();
  const showToast = useToasts((state) => state.show);
  const people = role.assignedUserCount;
  const isInUse = !role.isSystem && people > 0;

  const close = (): void => {
    closeMenu();
    setIsRenaming(false);
  };

  const deleteRole = (): void => {
    close();
    remove.mutate(role.id, {
      onSuccess: () => {
        showToast({ tone: 'success', message: `Role "${role.label}" deleted` });
        onDeleted();
      },
      onError: (error) => showToast({ tone: 'danger', message: error.message }),
    });
  };

  return (
    <header className={styles.header}>
      <div className={styles.top}>
        <div className={styles.text}>
          <div className={styles.nameLine}>
            <h2 className={styles.name}>{draft.label}</h2>
            <Chip tone="neutral" plain>
              {role.isSystem ? 'Standard role' : 'Custom role'}
            </Chip>
          </div>
          <p className={styles.origin}>{originLine(role)}</p>
          <p className={styles.description}>{draft.description.trim() === '' ? '—' : draft.description}</p>
          <p className={styles.reach}>{people === 0 ? 'Nobody has this role yet.' : `Changes apply to ${peopleText(people)}.`}</p>
        </div>
        {canEdit && (
          <Button
            ref={triggerRef}
            variant="ghost"
            size="sm"
            icon={MoreHorizontal}
            aria-label={`More for ${role.label}`}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            isPending={remove.isPending}
            onClick={() => (isOpen ? close() : open())}
          />
        )}
      </div>

      {isInUse && (
        <p className={styles.note}>
          <ShieldAlert size={14} aria-hidden />
          {people} {people === 1 ? 'person has' : 'people have'} this role, so it cannot be deleted until they are moved.
        </p>
      )}

      <Popover anchor={anchor} onClose={close} align="right" width={MENU_WIDTH} label={`More for ${role.label}`}>
        {isRenaming ? (
          <RenameForm
            draft={draft}
            onCancel={close}
            onDone={(label, description) => {
              onRename(label, description);
              close();
            }}
          />
        ) : (
          <MenuGroup>
            <MenuItem icon={<Pencil size={14} aria-hidden />} onClick={() => setIsRenaming(true)}>
              Rename
            </MenuItem>
            {!role.isSystem && (
              <MenuItem icon={<Trash2 size={14} aria-hidden />} isDanger disabled={isInUse} onClick={deleteRole}>
                Delete this role
              </MenuItem>
            )}
          </MenuGroup>
        )}
      </Popover>
    </header>
  );
}

function RenameForm({
  draft,
  onCancel,
  onDone,
}: {
  readonly draft: RoleDraft;
  readonly onCancel: () => void;
  readonly onDone: (label: string, description: string) => void;
}): ReactNode {
  const [label, setLabel] = useState(draft.label);
  const [description, setDescription] = useState(draft.description);
  const canSubmit = label.trim().length >= 2;

  return (
    <form
      className={styles.rename}
      onSubmit={(event) => {
        event.preventDefault();
        if (canSubmit) onDone(label, description);
      }}
    >
      <Field label="Name" isRequired>
        {({ inputId }) => <Input id={inputId} value={label} autoFocus maxLength={80} onChange={(event) => setLabel(event.target.value)} />}
      </Field>
      <Field label="What this role is for" isOptional>
        {({ inputId }) => <Textarea id={inputId} rows={2} maxLength={400} value={description} onChange={(event) => setDescription(event.target.value)} />}
      </Field>
      <p className={styles.renameHint}>Saved with your other changes.</p>
      <div className={styles.renameActions}>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="solid" type="submit" disabled={!canSubmit}>
          Done
        </Button>
      </div>
    </form>
  );
}
